import { Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../api';
import {
  vehicleService,
  Vehicle,
  maintenanceService,
  Maintenance,
  CreateMaintenanceData,
  Provider,
  providerService,
} from '../../api';
import { tripService, Trip } from '../../api/trips';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  formatCurrencyBRL,
  formatDatePtBR,
  parseDateFromInput,
  ptBRLocale,
  toDateInputValue,
  todayInputValue,
  addDaysToDate,
} from '../../utils/formatters';

registerLocale('pt-BR', ptBRLocale);
import {
  Wrench,
  Search,
  Plus,
  Car,
  Building2,
  CalendarDays,
  Clock,
  DollarSign,
  FileText,
  Edit2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Input,
  Button,
  Badge,
  Label,
  Select,
  Modal,
  ConfirmModal,
  showToast,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

type FieldWrapperProps = {
  children: ReactNode;
  error?: string;
  label?: ReactNode;
  htmlFor?: string;
  required?: boolean;
  hint?: string;
};

const FieldWrapper = ({ children, error, label, htmlFor, required, hint }: FieldWrapperProps) => (
  <div className="flex flex-col gap-1">
    {label ? (
      <Label htmlFor={htmlFor} className="mb-0.5">
        <span className="inline-flex items-center gap-0.5">
          {label}
          {required ? <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span> : null}
        </span>
      </Label>
    ) : null}
    {children}
    {hint && !error ? (
      <p className="mt-0.5 text-[11px] font-medium leading-tight text-slate-500">{hint}</p>
    ) : null}
    {error ? (
      <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold leading-tight text-rose-600">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        {error}
      </p>
    ) : null}
  </div>
);

const formatDate = (dateStr?: string) => formatDatePtBR(dateStr);

const formatCurrency = (value?: number) => formatCurrencyBRL(value);

const UserManutencao = () => {
  const { user, setPageTitle } = useAuth();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [myTrips, setMyTrips] = useState<Trip[]>([]);
  const [maintenances, setMaintenances] = useState<Maintenance[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<CreateMaintenanceData>>({
    vehicle_id: undefined,
    provider_id: undefined,
    reason: '',
    entry_date: '',
    estimated_completion: '',
    final_price: 0,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [editId, setEditId] = useState<number | null>(null);

  const employeeId = user?.employee_id;
  const userId = user?.id;

  useEffect(() => {
    setPageTitle('Minhas Manutenções');
  }, [setPageTitle]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [allVehicles, allProviders, allTrips, allMaintenances] =
        await Promise.all([
          vehicleService.getAllVehicles(),
          providerService.getAll(),
          tripService.getAll(),
          maintenanceService.getAll(),
        ]);

      const trips = (allTrips || []).filter(
        (t) => !!employeeId && t.employee_id === employeeId,
      );

      const tripsVehicleIds = new Set(trips.map((t) => t.vehicle_id));
      const myVehicles = (allVehicles || []).filter(
        (v) => tripsVehicleIds.has(v.id) || v.status === 'disponivel',
      );

      const myMaintenances = (allMaintenances || []).filter((m) => {
        if (userId && m.created_by_user?.id === userId) return true;
        if (employeeId) {
          const vehicleTrips = trips.filter((t) => t.vehicle_id === m.vehicle_id);
          return vehicleTrips.length > 0;
        }
        return false;
      });

      setVehicles(myVehicles);
      setMyTrips(trips);
      setProviders(allProviders || []);
      setMaintenances(myMaintenances);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao carregar manutenções.'));
    } finally {
      setLoading(false);
    }
  }, [employeeId, userId]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const filteredMaintenances = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return maintenances;
    return maintenances.filter((maintenance) => {
      const vehicle = maintenance.vehicle;
      const provider = maintenance.provider;
      const statusLabel =
        maintenance.status === 'pending' ? 'pendente' :
        maintenance.status === 'active' ? 'aprovada' :
        maintenance.status === 'rejected' ? 'rejeitada' :
        maintenance.status === 'concluded' ? 'concluída' : String(maintenance.status);
      return (
        (vehicle?.brand || '').toLowerCase().includes(term) ||
        (vehicle?.description || '').toLowerCase().includes(term) ||
        (vehicle?.license_plate || '').toLowerCase().includes(term) ||
        (provider?.name || '').toLowerCase().includes(term) ||
        (maintenance.reason || '').toLowerCase().includes(term) ||
        statusLabel.includes(term)
      );
    });
  }, [maintenances, searchTerm]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredMaintenances.length / ITEMS_PER_PAGE)),
    [filteredMaintenances.length],
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredMaintenances.length, currentPage, totalPages]);

  const { paginated, pageRange } = useMemo(() => {
    const total = filteredMaintenances.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      paginated: filteredMaintenances.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredMaintenances, currentPage, totalPages]);

  const getPageNumbers = () => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === 'vehicle_id' || name === 'provider_id' || name === 'final_price'
          ? value === '' || value === null
            ? undefined
            : Number(value)
          : value,
    }));
    if (formErrors[name]) setFormErrors((p) => { const n = { ...p }; delete n[name]; return n; });
  };

  const handleDateChange = (
    field: 'entry_date' | 'estimated_completion',
    date: Date | null,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date ? toDateInputValue(date) : '',
    }));
    if (formErrors[field]) setFormErrors((p) => { const n = { ...p }; delete n[field]; return n; });
  };

  const openCreateModal = (preselectedVehicleId?: number) => {
    setFormData({
      vehicle_id: preselectedVehicleId,
      provider_id: undefined,
      reason: '',
      entry_date: todayInputValue(),
      estimated_completion: '',
      final_price: 0,
    });
    setEditId(null);
    setFormErrors({});
    setError(null);
    setIsModalOpen(true);
  };

  const handleEdit = (maintenance: Maintenance) => {
    if (maintenance.status !== 'pending') return;
    setFormData({
      vehicle_id: maintenance.vehicle_id,
      provider_id: maintenance.provider_id,
      reason: maintenance.reason,
      entry_date: maintenance.entry_date.slice(0, 10),
      estimated_completion: maintenance.estimated_completion.slice(0, 10),
      final_price: maintenance.final_price,
    });
    setEditId(maintenance.id);
    setFormErrors({});
    setError(null);
    setIsModalOpen(true);
  };

  const closeFormModal = () => {
    setIsModalOpen(false);
    setFormErrors({});
    setFormData({
      vehicle_id: undefined,
      provider_id: undefined,
      reason: '',
      entry_date: '',
      estimated_completion: '',
      final_price: 0,
    });
    setEditId(null);
    setError(null);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId == null) return;
    const targetId = deleteConfirmId;
    setDeleteConfirmId(null);
    setLoading(true);
    setError(null);
    try {
      await maintenanceService.delete(targetId);
      await fetchData();
      showToast({
        type: 'success',
        title: 'Solicitação de manutenção cancelada',
      });
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao cancelar solicitação de manutenção.'));
      showToast({
        type: 'error',
        title: 'Não foi possível cancelar a solicitação',
        description: getApiErrorMessage(err, 'Tente novamente em alguns segundos.'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!formData.vehicle_id) nextErrors.vehicle_id = 'Selecione um veículo';
    if (!formData.provider_id) nextErrors.provider_id = 'Selecione um fornecedor';
    if (!formData.reason?.trim()) nextErrors.reason = 'Informe o motivo da manutenção';
    if (!formData.entry_date) nextErrors.entry_date = 'Informe a data de entrada';
    if (!formData.estimated_completion) nextErrors.estimated_completion = 'Informe a previsão de conclusão';
    if (formData.final_price === undefined || formData.final_price < 0) {
      nextErrors.final_price = 'Informe um valor válido (pode ser 0)';
    }
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        vehicle_id: Number(formData.vehicle_id),
        provider_id: Number(formData.provider_id),
        reason: formData.reason!.trim(),
        entry_date: new Date(formData.entry_date!).toISOString(),
        estimated_completion: new Date(formData.estimated_completion!).toISOString(),
        final_price: Number(formData.final_price || 0),
      };
      if (editId) {
        await maintenanceService.update(editId, payload);
      } else {
        await maintenanceService.create(payload as CreateMaintenanceData);
      }
      closeFormModal();
      await fetchData();
    } catch (err: unknown) {
      setError(
        getApiErrorMessage(err, 'Erro ao salvar solicitação de manutenção.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const tripSummaryForVehicle = (vehicleId: number) => {
    const vTrips = myTrips.filter((t) => t.vehicle_id === vehicleId);
    const lastTrip = vTrips
      .slice()
      .sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      )[0];
    return {
      count: vTrips.length,
      lastTrip,
    };
  };

  const canManage = (m: Maintenance) => {
    if (userId && m.created_by_user?.id === userId) return true;
    if (employeeId) {
      const vehicleTrips = myTrips.filter((t) => t.vehicle_id === m.vehicle_id);
      return vehicleTrips.length > 0;
    }
    return false;
  };

  const renderStatusBadge = (status: Maintenance['status']) => {
    switch (status) {
      case 'pending':
        return (
          <Badge tone="amber" className="gap-1.5">
            <Clock className="h-3 w-3 stroke-[2]" />
            Pendente
          </Badge>
        );
      case 'active':
        return (
          <Badge tone="blue" className="gap-1.5">
            <CheckCircle2 className="h-3 w-3 stroke-[2]" />
            Aprovada
          </Badge>
        );
      case 'rejected':
        return (
          <Badge tone="red" className="gap-1.5">
            <XCircle className="h-3 w-3 stroke-[2]" />
            Rejeitada
          </Badge>
        );
      case 'concluded':
        return (
          <Badge tone="emerald" className="gap-1.5">
            <CheckCircle2 className="h-3 w-3 stroke-[2]" />
            Concluída
          </Badge>
        );
      default:
        return (
          <Badge tone="slate">{String(status)}</Badge>
        );
    }
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Minhas Manutenções" hideTitleInBody />

        <div className="flex flex-col gap-5">
          <div className="mb-2 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[15px] font-semibold leading-tight text-sky-600">Operações — Manutenções</p>
              <h1 className="mt-1 text-[22px] sm:text-[26px] font-bold leading-[1.15] tracking-tight text-slate-900">
                Solicitações de manutenção do seu escopo.
                <span className="ml-1.5 text-slate-500 font-semibold text-[18px] sm:text-[20px]">
                  Envie para o cliente (gestor) aprovar.
                </span>
              </h1>
              <p className="mt-2 text-[14px] leading-relaxed text-slate-500 max-w-3xl">
                Crie solicitações para veículos que você utilizou em suas viagens. Após envio a solicitação fica pendente até aprovação do gestor da frota.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="primary" size="lg" onClick={() => openCreateModal()} disabled={submitting}>
                <Plus className="h-[18px] w-[18px] stroke-[2.1]" />
                Nova Solicitação
              </Button>
            </div>
          </div>

          {error && (
            <div className="inline-flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
              <div className="flex flex-col gap-0.5">
                <span className="text-[13px] font-bold leading-tight">Atenção</span>
                <span className="text-[12.5px] font-medium leading-tight text-rose-700/90">{error}</span>
              </div>
            </div>
          )}

          {myTrips.length === 0 && !loading && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-[18px] w-[18px] shrink-0 mt-[1px] stroke-[1.9]" />
                <div>
                  <p className="font-semibold mb-0.5">Sem viagens no seu escopo</p>
                  <p className="text-[13px] text-amber-800/90">
                    Você ainda não tem viagens concluídas, portanto não há veículos disponíveis para criar solicitações de manutenção.
                  </p>
                </div>
              </div>
            </div>
          )}

          <SectionCard>
            <CardHeader>
              <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-500 to-indigo-700 text-white shadow-[0_6px_16px_-8px_rgba(37,99,235,0.6)]">
                    <Wrench className="h-[20px] w-[20px] stroke-[1.9]" />
                  </div>
                  <div>
                    <h2 className="text-[15px] font-bold tracking-tight text-slate-900">Minhas solicitações</h2>
                    <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                      Todas as manutenções vinculadas aos veículos do seu histórico
                    </p>
                  </div>
                </div>
                <div className="relative w-full sm:w-[360px] shrink-0">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    placeholder="Pesquisar veículo, placa, fornecedor, motivo..."
                    className="!pl-10 h-10"
                  />
                </div>
              </div>
            </CardHeader>

            <CardBody>
              {loading && maintenances.length === 0 ? (
                <div className="flex items-center justify-center gap-2.5 py-16">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  <span className="text-[13.5px] font-semibold text-slate-600">Carregando manutenções...</span>
                </div>
              ) : (
                <ModernTable className="table-fixed layout-fixed">
                  <Thead>
                    <Tr>
                      <Th className="w-[16%] min-w-[200px] max-w-[260px]">Veículo</Th>
                      <Th className="w-[14%] min-w-[170px] max-w-[230px]">Fornecedor</Th>
                      <Th className="w-[22%] min-w-[220px] max-w-[380px]">Motivo</Th>
                      <Th className="w-[10%] min-w-[120px] max-w-[150px]">Entrada</Th>
                      <Th className="w-[11%] min-w-[120px] max-w-[160px]">Previsão</Th>
                      <Th className="w-[9%] min-w-[110px] max-w-[140px]">Valor</Th>
                      <Th className="w-[10%] min-w-[100px] max-w-[130px]">Status</Th>
                      <Th className="w-[8%] min-w-[120px] max-w-[150px]">Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginated.length === 0 ? (
                      <Tr>
                        <Td colSpan={8}>
                          <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                              <Wrench className="h-7 w-7 stroke-[1.8]" />
                            </div>
                            <div className="text-[14px] font-bold text-slate-700">Nenhuma solicitação encontrada</div>
                            <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                              {searchTerm
                                ? 'Nenhum resultado para a busca atual. Tente outro termo.'
                                : 'Clique em "Nova Solicitação" para enviar a primeira manutenção ao gestor.'}
                            </p>
                          </div>
                        </Td>
                      </Tr>
                    ) : (
                      paginated.map((m) => {
                        const vehicle = m.vehicle || vehicles.find(v => v.id === m.vehicle_id);
                        const provider = m.provider || providers.find(p => p.id === m.provider_id);
                        const canEdit = m.status === 'pending' && canManage(m);
                        return (
                          <Tr key={m.id}>
                            <Td>
                              <div className="min-w-0 w-full max-w-[260px] overflow-hidden">
                                <div className="flex items-start gap-2.5">
                                  <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                                    <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={vehicle?.brand || '-'}>
                                      {vehicle?.brand || `Veículo #${m.vehicle_id}`}
                                    </span>
                                    <div className="mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500 inline-flex items-center gap-1">
                                      <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-slate-600 font-[11px] font-semibold tabular-nums tracking-wide">
                                        {vehicle?.license_plate || 'Sem placa'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="min-w-0 w-full max-w-[230px] overflow-hidden">
                                <div className="flex items-start gap-2">
                                  <Building2 className="mt-[3px] h-3.5 w-3.5 shrink-0 text-slate-400 stroke-[1.8]" />
                                  <span className="block truncate text-[13px] font-semibold leading-tight text-slate-900" title={provider?.name || '-'}>
                                    {provider?.name || '-'}
                                  </span>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="min-w-0 w-full max-w-[380px] overflow-hidden">
                                <div className="flex items-start gap-2">
                                  <FileText className="mt-[3px] h-3.5 w-3.5 shrink-0 text-slate-400 stroke-[1.8]" />
                                  <span className="block truncate text-[13px] font-medium leading-tight text-slate-800" title={m.reason}>
                                    {m.reason}
                                  </span>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                <CalendarDays className="h-[15px] w-[15px] text-slate-400 stroke-[1.8]" />
                                {formatDate(m.entry_date)}
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                <Clock className="h-[15px] w-[15px] text-slate-400 stroke-[1.8]" />
                                {formatDate(m.estimated_completion)}
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-center gap-1.5 text-[13px] font-bold text-slate-900 tabular-nums">
                                <DollarSign className="h-[15px] w-[15px] text-emerald-600 stroke-[1.8]" />
                                {formatCurrency(m.final_price)}
                              </div>
                            </Td>
                            <Td>
                              {renderStatusBadge(m.status)}
                            </Td>
                            <Td>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEdit(m)}
                                  disabled={!canEdit}
                                  title={canEdit ? 'Editar solicitação' : 'Edição bloqueada após aprovação'}
                                  className="h-8 w-8 rounded-lg text-slate-500 hover:text-blue-700 hover:bg-blue-50 hover:ring-blue-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <Edit2 className="h-4 w-4 stroke-[1.9]" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setDeleteConfirmId(m.id)}
                                  disabled={!canEdit}
                                  title={canEdit ? 'Cancelar solicitação' : 'Cancelamento bloqueado'}
                                  className="h-8 w-8 rounded-lg text-slate-500 hover:text-rose-700 hover:bg-rose-50 hover:ring-rose-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <Trash2 className="h-4 w-4 stroke-[1.9]" />
                                </Button>
                              </div>
                            </Td>
                          </Tr>
                        );
                      })
                    )}
                  </Tbody>
                </ModernTable>
              )}

              {pageRange.total > 0 ? (
                <div className="mt-4 flex flex-col gap-3 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-[12.5px] font-medium leading-tight text-slate-500">
                    Mostrando{' '}
                    <span className="font-bold tabular-nums text-slate-800">{pageRange.start}</span>
                    {'–'}{' '}
                    <span className="font-bold tabular-nums text-slate-800">{pageRange.end}</span>
                    {' de '}
                    <span className="font-bold tabular-nums text-slate-800">{pageRange.total}</span>
                    {' solicitação(ões)'}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                      aria-label="Primeira página"
                    >
                      <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                      aria-label="Página anterior"
                    >
                      <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                    </button>
                    <div className="inline-flex items-center gap-1 mx-1">
                      {getPageNumbers().map((n, idx) =>
                        n === '...' ? (
                          <span key={`dots-${idx}`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]">…</span>
                        ) : (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setCurrentPage(n)}
                            disabled={submitting}
                            className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1 ${
                              n === currentPage
                                ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                                : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                            }`}
                          >
                            {n}
                          </button>
                        ),
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                      aria-label="Próxima página"
                    >
                      <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                      aria-label="Última página"
                    >
                      <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                    </button>
                  </div>
                </div>
              ) : null}
            </CardBody>
          </SectionCard>
        </div>

        {isModalOpen && (
          <Modal
            onClose={closeFormModal}
            maxWidthClass="max-w-2xl"
            title={
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-500 to-indigo-700 text-white shadow-[0_6px_16px_-8px_rgba(37,99,235,0.6)]">
                  <Wrench className="h-5 w-5 stroke-[1.9]" />
                </div>
                <div>
                  <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                    {editId ? 'Editar solicitação' : 'Nova solicitação de manutenção'}
                  </div>
                  <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                    Preencha os campos abaixo para {editId ? 'atualizar os dados' : 'solicitar uma nova manutenção'}
                  </div>
                </div>
              </div>
            }
            footer={
              <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
                <button
                  type="button"
                  onClick={closeFormModal}
                  disabled={submitting}
                  className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30 disabled:pointer-events-none disabled:opacity-60"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  form="manutencao-form"
                  disabled={submitting}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-blue-600 via-indigo-600 to-indigo-700 px-6 text-[13px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)] transition-all hover:shadow-[0_8px_22px_-6px_rgba(37,99,235,0.65)] focus:outline-none focus:ring-4 focus:ring-blue-500/25 disabled:pointer-events-none disabled:opacity-60 disabled:shadow-none"
                >
                  {submitting
                    ? <Fragment><Loader2 className="h-4 w-4 animate-spin stroke-[2]" /> Salvando...</Fragment>
                    : editId
                      ? 'Salvar alterações'
                      : 'Enviar para aprovação'}
                </button>
              </div>
            }
          >
            <form id="manutencao-form" onSubmit={handleSubmit} className="space-y-5" noValidate>
              {error && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                  {error}
                </div>
              )}
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                  <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">Dados do veículo e fornecedor</h3>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <FieldWrapper
                      label={<span className="inline-flex items-center gap-1"><Car className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Veículo</span>}
                      htmlFor="manutencao-veiculo"
                      required
                      error={formErrors.vehicle_id}
                    >
                      <Select
                        id="manutencao-veiculo"
                        name="vehicle_id"
                        value={formData.vehicle_id ?? ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Selecione um veículo</option>
                        {vehicles.map((v) => {
                          const summary = tripSummaryForVehicle(v.id);
                          const label =
                            `${v.brand || 'Veículo'} — ${v.license_plate || 'sem placa'}` +
                            (summary.count
                              ? ` (${summary.count} viagem${summary.count === 1 ? '' : 's'} sua)`
                              : '');
                          return (
                            <option key={v.id} value={v.id}>
                              {label}
                            </option>
                          );
                        })}
                      </Select>
                      {myTrips.length === 0 && vehicles.length === 0 ? (
                        <span className="text-[11.5px] font-medium leading-tight text-amber-700">
                          Você ainda não tem viagens concluídas — veículos indisponíveis.
                        </span>
                      ) : null}
                    </FieldWrapper>
                    <FieldWrapper
                      label={<span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Fornecedor / Oficina</span>}
                      htmlFor="manutencao-provider"
                      required
                      error={formErrors.provider_id}
                    >
                      <Select
                        id="manutencao-provider"
                        name="provider_id"
                        value={formData.provider_id ?? ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Selecione o fornecedor</option>
                        {providers.length === 0
                          ? <option value="" disabled>Nenhum fornecedor cadastrado</option>
                          : providers.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.name}{p.phone ? ` (${p.phone})` : ''}
                            </option>
                          ))}
                      </Select>
                    </FieldWrapper>
                  </div>
                  <FieldWrapper
                    label={<span className="inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Motivo / Descrição</span>}
                    htmlFor="manutencao-motivo"
                    required
                    error={formErrors.reason}
                  >
                    <textarea
                      id="manutencao-motivo"
                      name="reason"
                      rows={4}
                      value={formData.reason ?? ''}
                      onChange={handleInputChange}
                      placeholder="Descreva o defeito, problema percebido, itens para revisão..."
                      className="w-full resize-y rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-[13.5px] text-slate-900 transition focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                    />
                  </FieldWrapper>
                </div>
              </div>
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-teal-600" />
                  <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-emerald-700">Datas e orçamento</h3>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <FieldWrapper
                      label={<span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Data de entrada</span>}
                      htmlFor="manutencao-entry"
                      required
                      error={formErrors.entry_date}
                    >
                      <DatePicker
                        selected={formData.entry_date ? parseDateFromInput(formData.entry_date) : null}
                        onChange={(date) => handleDateChange('entry_date', date)}
                        locale="pt-BR"
                        dateFormat="dd/MM/yyyy"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        className={`w-full h-10 rounded-lg border px-3.5 text-[13px] text-slate-900 outline-none transition ${
                          formErrors.entry_date
                            ? 'border-rose-300 focus:border-rose-500 focus:ring-4 focus:ring-rose-500/10'
                            : 'border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10'
                        }`}
                        placeholderText="Selecione a data"
                        calendarStartDay={0}
                      />
                    </FieldWrapper>
                    <FieldWrapper
                      label={<span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Previsão de conclusão</span>}
                      htmlFor="manutencao-estimate"
                      required
                      error={formErrors.estimated_completion}
                    >
                      <DatePicker
                        selected={formData.estimated_completion ? parseDateFromInput(formData.estimated_completion) : null}
                        onChange={(date) => handleDateChange('estimated_completion', date)}
                        locale="pt-BR"
                        dateFormat="dd/MM/yyyy"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        minDate={
                          formData.entry_date
                            ? addDaysToDate(parseDateFromInput(formData.entry_date), 1) ?? undefined
                            : undefined
                        }
                        className={`w-full h-10 rounded-lg border px-3.5 text-[13px] text-slate-900 outline-none transition ${
                          formErrors.estimated_completion
                            ? 'border-rose-300 focus:border-rose-500 focus:ring-4 focus:ring-rose-500/10'
                            : 'border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10'
                        }`}
                        placeholderText="Selecione a data"
                        calendarStartDay={0}
                      />
                    </FieldWrapper>
                  </div>
                  <FieldWrapper
                    label={<span className="inline-flex items-center gap-1"><DollarSign className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Valor orçado (R$)</span>}
                    htmlFor="manutencao-price"
                    required
                    error={formErrors.final_price}
                    hint="Informe 0 caso ainda não exista orçamento definido."
                  >
                    <Input
                      id="manutencao-price"
                      type="number"
                      name="final_price"
                      value={formData.final_price === undefined || formData.final_price === 0 ? '' : formData.final_price}
                      onChange={handleInputChange}
                      placeholder="0,00"
                      min="0"
                      step="0.01"
                      className="max-w-xs"
                    />
                  </FieldWrapper>
                </div>
              </div>
            </form>
          </Modal>
        )}

        <ConfirmModal
          isOpen={deleteConfirmId !== null}
          title="Cancelar solicitação de manutenção?"
          description="Esta ação não pode ser desfeita. A solicitação será removida permanentemente e o setor de operações não receberá mais esta pendência."
          confirmVariant="danger"
          confirmLabel="Sim, cancelar solicitação"
          cancelLabel="Manter solicitação"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteConfirmId(null)}
        />
      </div>
    </div>
  );
};

export default UserManutencao;
